import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, HeartHandshake } from "lucide-react";
import api from "../lib/api";
import { deDateFull, deDateMediumTime } from "../lib/dateFormat";

/**
 * Feb 2026 · Print-optimised booking-prep sheet for the concierge.
 *
 * Loads the aggregated `/api/team/bookings/{id}/print` payload and renders
 * a clean, single-column A4 layout: property + dates + guest, PROMINENT
 * preferences summary (allergies + food + home + notes), then the full
 * service list (inclusive + extras) with hand-tickable checkboxes, plus
 * both personal Abreise/Anreise checklists (also hand-tickable).
 *
 * Auto-triggers the browser print dialog on first mount so the concierge
 * can go directly from the modal-button to "Save as PDF" or "Print".
 *
 * Route: `/team/print/booking/:id` — outside the Layout wrapper so no
 * sidebar / header chrome leaks onto the printed sheet.
 */
export default function TeamBookingPrint() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [autoPrinted, setAutoPrinted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: payload } = await api.get(`/team/bookings/${id}/print`);
        if (cancelled) return;
        setData(payload);
      } catch (e) {
        if (cancelled) return;
        setErr(e.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!data || autoPrinted) return;
    // Give the browser one paint cycle to lay out the page before we
    // invoke the print dialog — otherwise Chrome sometimes prints a
    // blank first page while React is still hydrating.
    const t = setTimeout(() => {
      window.print();
      setAutoPrinted(true);
    }, 400);
    return () => clearTimeout(t);
  }, [data, autoPrinted]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-8">
        <div className="max-w-md text-center">
          <div className="label-caps mb-2 text-red-700">Fehler</div>
          <div className="text-navy-dark">{err}</div>
          <Link to="/team/bookings" className="mt-4 inline-block text-[12px] tracking-widest uppercase text-navy-dark/70 hover:text-navy">
            ← Zurück zu Buchungen
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-navy-dark/60 label-caps">
        Wird geladen …
      </div>
    );
  }

  const { booking, property, customer, preferences, standard_services, extras, generated_at, generated_by } = data;
  const disabledStd = new Set(booking.disabled_std_service_keys || []);
  const bookedExtraKeys = new Set(booking.service_ids || []);
  const serviceNotes = booking.service_notes || {};
  const dep = booking.departure_checklist || [];
  const arr = booking.arrival_checklist || [];

  const fmtDate = (iso) => {
    try {
      return deDateFull(iso);
    } catch { return iso; }
  };
  const nights = (() => {
    try {
      const a = new Date(booking.from_date + "T00:00:00").getTime();
      const b = new Date(booking.to_date + "T00:00:00").getTime();
      return Math.max(1, Math.round((b - a) / 86400000));
    } catch { return 1; }
  })();

  const stdByCategory = (standard_services || []).reduce((acc, s) => {
    const cat = s.subcategory || "management";
    (acc[cat] = acc[cat] || []).push(s);
    return acc;
  }, {});
  const extrasByCategory = (extras || []).reduce((acc, e) => {
    const cat = e.category || "additional";
    (acc[cat] = acc[cat] || []).push(e);
    return acc;
  }, {});

  return (
    <>
      <style>{`
        /* Screen: soft ivory frame around the sheet so the concierge sees
           exactly what will print. Print: hides the toolbar + trims to
           A4 with sensible margins. */
        @media screen {
          body { background: #F7F1E4; }
        }
        @media print {
          body { background: #FFFFFF !important; }
          .no-print { display: none !important; }
          .print-sheet { box-shadow: none !important; margin: 0 !important; padding: 12mm 14mm !important; }
          @page { size: A4; margin: 10mm; }
        }
        .print-sheet {
          font-family: Georgia, "Times New Roman", serif;
          color: #141B2D;
          line-height: 1.55;
        }
        .print-sheet .kicker { font-size: 10.5px; letter-spacing: 0.18em; text-transform: uppercase; color: #8A7635; }
        .print-sheet .h1 { font-size: 26px; margin: 4px 0 12px; font-weight: 400; }
        .print-sheet .h2 { font-size: 15px; text-transform: uppercase; letter-spacing: 0.14em; color: #141B2D; margin: 22px 0 8px; }
        .print-sheet .rule { border-top: 1px solid #E8DFC9; margin: 4px 0 12px; }
        .print-sheet .chip { display: inline-block; padding: 2px 10px; margin: 0 4px 4px 0; border: 1px solid #C9A961; background: #F5EEDB; color: #141B2D; font-size: 12px; }
        .print-sheet .box {
          display: inline-block; width: 12px; height: 12px; border: 1.4px solid #141B2D;
          margin-right: 8px; vertical-align: middle;
        }
        .print-sheet .row { display: flex; align-items: flex-start; gap: 6px; padding: 3px 0; page-break-inside: avoid; }
        .print-sheet .row .lbl { flex: 1; font-size: 13.5px; }
        .print-sheet .row .note { color: #3D4756; font-style: italic; font-size: 12.5px; margin-left: 6px; }
        .print-sheet .cat-hdr { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: #3D4756; margin-top: 10px; margin-bottom: 4px; }
        .print-sheet .pref-block { border: 1.4px solid #C9A961; padding: 12px 14px; background: #FFFBF1; margin-bottom: 6px; }
        .print-sheet .pref-block .pref-row { padding: 3px 0; font-size: 13.5px; }
        .print-sheet .pref-block .pref-row .k { display: inline-block; width: 92px; font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase; color: #8A7635; vertical-align: top; }
        .print-sheet .meta-row { display: flex; justify-content: space-between; font-size: 11.5px; color: #6C7480; margin-top: 22px; padding-top: 10px; border-top: 1px solid #E8DFC9; }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-ivory border-b border-navy/10 px-6 py-3 flex items-center justify-between">
        <Link to={-1} onClick={(e) => { e.preventDefault(); window.close(); if (!window.closed) window.history.back(); }} className="text-[11px] tracking-widest uppercase text-navy-dark/70 hover:text-navy inline-flex items-center gap-1.5" data-testid="print-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Zurück
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-navy text-ivory px-5 py-2 text-[11px] tracking-[0.18em] uppercase hover:opacity-90"
          data-testid="print-btn"
        >
          <Printer className="w-3.5 h-3.5" strokeWidth={1.6} /> Drucken
        </button>
      </div>

      <div className="max-w-[210mm] mx-auto my-6 bg-white shadow-sm print-sheet" style={{ padding: "16mm 18mm" }} data-testid="team-booking-print-sheet">
        {/* Header */}
        <div className="kicker">Weissblom · Vorbereitungs-Checkliste</div>
        <div className="h1">{property?.name || "Objekt"}</div>
        <div style={{ fontSize: 13.5, color: "#3D4756" }}>
          <div><strong>Gast:</strong> {customer?.name || "—"}</div>
          <div><strong>Zeitraum:</strong> {fmtDate(booking.from_date)} → {fmtDate(booking.to_date)} · {nights} {nights === 1 ? "Nacht" : "Nächte"}</div>
          {booking.notes && (
            <div style={{ marginTop: 6, fontStyle: "italic" }}>
              <strong style={{ fontStyle: "normal" }}>Kunden-Notiz: </strong>{booking.notes}
            </div>
          )}
        </div>

        {/* Preferences — always prominent even when empty, so the concierge
            reliably scans this section on every printed sheet. */}
        <div className="h2" data-testid="print-preferences-heading">
          <HeartHandshake className="w-3.5 h-3.5 inline-block mr-1.5" strokeWidth={1.6} style={{ color: "#8A7635", verticalAlign: "-2px" }} />
          Präferenzen des Haushalts
        </div>
        <div className="pref-block" data-testid="print-preferences-block">
          <PrefLine k="Allergien" v={preferences?.allergies} />
          <PrefLine k="Ernährung" v={preferences?.food} />
          <PrefLine k="Zuhause" v={preferences?.home} />
          {(preferences?.notes || "").trim() && (
            <div className="pref-row"><span className="k">Notiz</span><em>{preferences.notes}</em></div>
          )}
          {(!preferences?.allergies?.length && !preferences?.food?.length && !preferences?.home?.length && !(preferences?.notes || "").trim()) && (
            <div className="pref-row" style={{ color: "#6C7480", fontStyle: "italic" }}>Keine Präferenzen hinterlegt — bitte im Kundenprofil ergänzen.</div>
          )}
        </div>

        {/* Departure checklist — personal customer to-dos, kept for the concierge's context */}
        {dep.length > 0 && (
          <>
            <div className="h2" data-testid="print-departure-heading">Abreise (Kunde)</div>
            <div className="rule" />
            {dep.map((item) => (
              <div key={item.key} className="row">
                <span className="box" />
                <span className="lbl" style={{ textDecoration: item.done ? "line-through" : "none", color: item.done ? "#6C7480" : "#141B2D" }}>
                  {item.label}
                </span>
              </div>
            ))}
          </>
        )}

        {/* Inclusive services (STANDARD services from master catalog) */}
        <div className="h2" data-testid="print-inclusive-heading">Inklusiv-Leistungen</div>
        <div className="rule" />
        {["interior", "outdoor", "management"].map((cat) => {
          const list = stdByCategory[cat];
          if (!list?.length) return null;
          return (
            <div key={cat}>
              <div className="cat-hdr">
                {cat === "interior" ? "Innenbereich" : cat === "outdoor" ? "Außenbereich" : "Verwaltung"}
              </div>
              {list.map((s) => {
                const disabled = disabledStd.has(s.key);
                const note = serviceNotes[s.key];
                return (
                  <div key={s.key} className="row">
                    <span className="box" style={{ background: disabled ? "#EBEBEB" : "transparent", borderColor: disabled ? "#B0B0B0" : "#141B2D" }} />
                    <span className="lbl" style={{ textDecoration: disabled ? "line-through" : "none", color: disabled ? "#8A9099" : "#141B2D" }}>
                      {s.name_de || s.name_en}
                      {disabled && <span className="note">— vom Kunden abgewählt</span>}
                      {note && !disabled && <span className="note">„{note}"</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Extras — only ones the customer booked */}
        {["interior", "outdoor", "additional"].some((c) => (extrasByCategory[c] || []).some((e) => bookedExtraKeys.has(e.id))) && (
          <>
            <div className="h2" data-testid="print-extras-heading">Gebuchte Zusatzleistungen</div>
            <div className="rule" />
            {["interior", "outdoor", "additional"].map((cat) => {
              const list = (extrasByCategory[cat] || []).filter((e) => bookedExtraKeys.has(e.id));
              if (!list.length) return null;
              return (
                <div key={cat}>
                  <div className="cat-hdr">
                    {cat === "interior" ? "Innenbereich" : cat === "outdoor" ? "Außenbereich" : "Zusätzlich"}
                  </div>
                  {list.map((ex) => {
                    const note = serviceNotes[ex.id];
                    return (
                      <div key={ex.id} className="row">
                        <span className="box" />
                        <span className="lbl">
                          {ex.name_de || ex.name_en}
                          {note && <span className="note">„{note}"</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </>
        )}

        {/* Arrival checklist — personal customer to-dos for return */}
        {arr.length > 0 && (
          <>
            <div className="h2" data-testid="print-arrival-heading">Ankunft (Kunde)</div>
            <div className="rule" />
            {arr.map((item) => (
              <div key={item.key} className="row">
                <span className="box" />
                <span className="lbl" style={{ textDecoration: item.done ? "line-through" : "none", color: item.done ? "#6C7480" : "#141B2D" }}>
                  {item.label}
                </span>
              </div>
            ))}
          </>
        )}

        <div className="meta-row">
          <div>Erstellt von {generated_by || "—"}</div>
          <div>
            {deDateMediumTime(generated_at)}
          </div>
        </div>
      </div>
    </>
  );
}

function PrefLine({ k, v }) {
  if (!v || v.length === 0) return null;
  return (
    <div className="pref-row" data-testid={`print-pref-${k.toLowerCase()}`}>
      <span className="k">{k}</span>
      {v.map((t, i) => <span key={`${t}-${i}`} className="chip">{t}</span>)}
    </div>
  );
}
